import React, { useState, useEffect } from 'react';
import { ownerAPI, fieldAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Users, Banknote, Phone, Calendar, Loader2, Filter } from 'lucide-react';

export default function OwnerBookings() {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [fields, setFields] = useState([]);
  const [filter, setFilter] = useState({ status: 'all', field: 'all' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bookingsRes, fieldsRes] = await Promise.all([
        ownerAPI.getBookings({}),
        fieldAPI.getMy()
      ]);
      setBookings(bookingsRes.data);
      setFields(fieldsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id) => {
    try {
      await ownerAPI.confirmBooking(id);
      toast.success('Buchung bestätigt');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleReject = async (id) => {
    try {
      await ownerAPI.rejectBooking(id);
      toast.success('Buchung abgelehnt - Spieler wurde erstattet');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleComplete = async (id) => {
    try {
      await ownerAPI.completeBooking(id);
      toast.success('Buchung abgeschlossen - Einnahmen gutgeschrieben');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (filter.status !== 'all' && b.status !== filter.status) return false;
    if (filter.field !== 'all' && b.field_id !== filter.field) return false;
    return true;
  });

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      completed: 'Abgeschlossen',
      rejected: 'Abgelehnt',
      cancelled: 'Storniert'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-tunis-blue">Buchungen verwalten</h1>
            <p className="text-muted-foreground">{filteredBookings.length} Buchungen</p>
          </div>
          
          <div className="flex gap-2">
            <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
              <SelectTrigger className="w-[150px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="confirmed">Bestätigt</SelectItem>
                <SelectItem value="completed">Abgeschlossen</SelectItem>
                <SelectItem value="rejected">Abgelehnt</SelectItem>
                <SelectItem value="cancelled">Storniert</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.field} onValueChange={(v) => setFilter({...filter, field: v})}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Platz wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Plätze</SelectItem>
                {fields.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length > 0 ? (
            filteredBookings.map((booking) => (
              <Card key={booking.id} className={`border-l-4 ${booking.status === 'pending' ? 'border-l-yellow-500' : booking.status === 'confirmed' ? 'border-l-green-500' : booking.status === 'completed' ? 'border-l-blue-500' : 'border-l-gray-300'}`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">{booking.field_name}</h3>
                        {statusBadge(booking.status)}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{booking.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{booking.start_time} - {booking.end_time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span>{booking.user_name}</span>
                        </div>
                        {booking.user_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{booking.user_phone}</span>
                          </div>
                        )}
                      </div>
                      
                      {booking.notes && (
                        <p className="text-sm text-muted-foreground mt-2">Notiz: {booking.notes}</p>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-tunis-teal">{(booking.total_price || 0).toFixed(2)} TND</p>
                        <p className="text-sm text-muted-foreground">
                          Ihre Einnahme: {(booking.owner_earnings || 0).toFixed(2)} TND
                        </p>
                      </div>
                      
                      {booking.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleConfirm(booking.id)}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Bestätigen
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleReject(booking.id)}>
                            <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                          </Button>
                        </div>
                      )}
                      
                      {booking.status === 'confirmed' && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => handleComplete(booking.id)}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Als abgeschlossen markieren
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Keine Buchungen gefunden</h3>
                <p className="text-muted-foreground">Es gibt keine Buchungen mit den ausgewählten Filtern</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
